
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  description: 'For a more robust Plaid integration, use this launch checklist, which consists of recommended pre-production steps for your Plaid app.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Checkbox = makeShortcode("Checkbox");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Launch in Production checklist' subtitle='Check off these recommended steps before launching your app in Production' mdxType="Header" />
    <Callout mdxType="Callout">
      <p>{`The Launch Checklist has been replaced by the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/launch-center"
        }}>{`Launch Center`}</a>{`. The Launch Center shows a personalized list of steps for your integration, with links to supporting materials like videos, sample code, and docs. The Launch Checklist is no longer being updated.`}</p>
    </Callout>
    <p>{`Below is a list of recommended steps to take before launching your Plaid integration in production. While they might not all be required for the minimal operation of your application, the steps below will help to make your Plaid integration more robust, secure, efficient, and maintainable.`}</p>
    <h4 {...{
      "id": "production-setup"
    }}>{`Production setup`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`If you haven't already done so, `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/overview/production"
        }}>{`request Production access`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Complete your `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/settings/company/app-branding"
        }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/settings/company/profile"
        }}>{`company profile`}</a>{`, which are required to access certain institutions that use OAuth-based connections.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Complete your `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/settings/company/compliance?tab=dataSecurity"
        }}>{`security questionnaire`}</a>{`, which is required to access certain US institutions that use OAuth-based connections.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`For European Union countries and the UK, there is a separate compliance process required to access financial institutions in Production.  If your Plaid integration will support financial institutions in Europe (including the UK), and your business is not based in Europe, `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/admin/account-administration/request-product-access"
        }}>{`file a support ticket`}</a>{` to request Production access for Europe.  Allow at least one week for your request to be processed.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Configure both Plaid Link and your server-side code to use the Production
environment by setting the appropriate value when configuring your client object or (if not using a client library) your HTTP request. For language-specific details, see the GitHub page for your client library, or `}<a parentName="p" {...{
          "href": "/docs/api/#api-host"
        }}>{`API host information`}</a>{` if not using a library.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Ensure you are using your Production secret, which can be found on the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/team/api"
        }}>{`dashboard`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If migrating from Sandbox, ensure that you remove any usage of Sandbox-specific functionality, such as the `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` test user or calls to `}<inlineCode parentName="p">{`/sandbox/`}</inlineCode>{` endpoints.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Add teammates to the dashboard to give other users access. For more details, see `}<a parentName="p" {...{
          "href": "/docs/account/teams"
        }}>{`Plaid teams`}</a>{`.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "link-setup"
    }}>{`Link setup`}</h4>
    <p>{`Follow the steps below to assist users in completing the Link flow, help ensure compliance with Plaid's policies, and avoid being billed for unneeded products.`}</p>
    <Checkbox mdxType="Checkbox">
      <p><a parentName="p" {...{
          "href": "/docs/link/oauth/"
        }}>{`Implement OAuth support`}</a>{`. Some institutions in both Europe and the US require the use of OAuth-based connections. While OAuth will typically "just work" on desktop, as long as you have completed your `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/settings/company/app-branding"
        }}>{`application profile`}</a>{`, `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/settings/company/profile"
        }}>{`company profile`}</a>{`, and `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/overview/questionnaire-start"
        }}>{`security questionnaire`}</a>{`, you may need to implement client-side redirect logic for users on mobile devices.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Make sure your OAuth integration works with all the `}<a parentName="p" {...{
          "href": "/docs/link/oauth/#testing-oauth"
        }}>{`test cases`}</a>{` recommended in the OAuth documentation.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`To avoid unnecessary billing and user confusion, implement logic for preventing `}<a parentName="p" {...{
          "href": "/docs/link/duplicate-items"
        }}>{`duplicate Items`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Configure Link customizations within the
`}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/link"
        }}>{`dashboard`}</a>{` and ensure that the countries and languages selected for the customizations match the settings provided to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. For more details, see `}<a parentName="p" {...{
          "href": "/docs/link/customization"
        }}>{`Link customization`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Ensure the `}<inlineCode parentName="p">{`client_name`}</inlineCode>{` parameter of `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` is set
to display your company's name as you'd like it to appear within Link.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Ensure that the `}<inlineCode parentName="p">{`products`}</inlineCode>{` parameter of `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` includes only the
products you intend to use. The products listed here will influence which
institutions and accounts appear in Link (only institutions and account types that support all specified products will
appear in Link) and will trigger a billing event for listed products upon
successful token exchange.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If you are using multiple Plaid products, see the recommendations in `}<a parentName="p" {...{
          "href": "/docs/link/initializing-products/"
        }}>{`Choosing how to initialize products`}</a>{` to make sure your Link sessions are optimized for performance, conversion, and billing.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Provide any notice and obtain any consent required for Plaid to process end user
information in accordance with Plaid's `}<a parentName="p" {...{
          "href": "/legal/#end-user-privacy-policy"
        }}>{`End User Privacy Policy`}</a>{`.`}</p>
    </Checkbox>
    <p>{`You may find it helpful to know that some of our customers link to Plaid's privacy policy within their own privacy policy, while others surface a separate just-in-time notice and consent page during their onboarding flow. Ultimately, it is up to you to determine how to obtain any legally required consents.`}</p>
    <Checkbox mdxType="Checkbox">
      <p>{`To maximize the number of users who complete the Link flow, review Plaid's recommendations for `}<a parentName="p" {...{
          "href": "/docs/link/best-practices"
        }}>{`optimizing Link conversion`}</a>{` and implement any practices relevant to your use case, such as providing `}<a parentName="p" {...{
          "href": "/docs/link/messaging/"
        }}>{`pre-Link messaging`}</a>{`.`}</p>
    </Checkbox>
    <h5 {...{
      "id": "callbacks"
    }}>{`Callbacks`}</h5>
    <p>{`Handle callbacks beyond just `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` in order to gracefully handle errors and build analytics around Link.`}</p>
    <Checkbox mdxType="Checkbox">
      <p>{`Listen to the `}<a parentName="p" {...{
          "href": "/docs/link/web#onexit"
        }}><inlineCode parentName="a">{`onExit()`}</inlineCode></a>{` and `}<a parentName="p" {...{
          "href": "/docs/link/web#onevent"
        }}><inlineCode parentName="a">{`onEvent()`}</inlineCode></a>{` callbacks for `}<inlineCode parentName="p">{`error_type`}</inlineCode>{` and `}<inlineCode parentName="p">{`error_code`}</inlineCode>{` in order to implement error handling.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Listen to the `}<a parentName="p" {...{
          "href": "/docs/link/web#onevent"
        }}><inlineCode parentName="a">{`onEvent()`}</inlineCode></a>{` callback for
`}<inlineCode parentName="p">{`exit_status`}</inlineCode>{` or `}<inlineCode parentName="p">{`timestamp`}</inlineCode>{` in order to implement Link conversion analytics. Alternatively, you can use Plaid's built-in `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/link-analytics"
        }}>{`Link conversion analytics`}</a>{` in the Dashboard.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "webhook-configuration"
    }}>{`Webhook configuration`}</h4>
    <p>{`Plaid uses webhooks for many common flows. If your integration does any of the following, webhooks are required or strongly recommended:`}</p>
    <ul>
      <li parentName="ul">{`(Any product) Your application is calling a Plaid endpoint for an Item repeatedly, over a period of time, not just immediately after Link.`}</li>
      <li parentName="ul">{`(Transactions or Investments) You are accessing transactions made after the end user linked their Item.`}</li>
      <li parentName="ul">{`(Auth) You are using automated micro-deposits for account verification, or making transfers over a year after the Item was initially linked.`}</li>
      <li parentName="ul">{`(Assets) You are creating Asset Reports.`}</li>
      <li parentName="ul">{`(Income) You are verifying a user's income.`}</li>
      <li parentName="ul">{`(Payment Initiation (UK and Europe)) You are initiating payments.`}</li>
      <li parentName="ul">{`(Virtual Accounts (UK and Europe) or Transfer) You are sending or receiving funds.`}</li>
    </ul>
    <Checkbox mdxType="Checkbox">
      <p>{`Make sure Link has been initialized with your URL for receiving webhooks via `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`(For Identity Verification, Transfer or Payment Initiation, as well as `}<a parentName="p" {...{
          "href": "/docs/auth/coverage/microdeposit-events/"
        }}>{`optional Auth micro-deposit events`}</a>{`) Make sure to configure account level webhook URLs via the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/webhooks"
        }}>{`Plaid Dashboard`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Make sure you can receive webhooks from `}<a parentName="p" {...{
          "href": "/docs/api/webhooks"
        }}>{`Plaid's webhook IPs`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Review Plaid's `}<a parentName="p" {...{
          "href": "/docs/api/webhooks/#best-practices-for-applications-using-webhooks"
        }}>{`webhook best practices`}</a>{` to ensure your webhook handling logic is robust to outages and traffic spikes.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`For all Item-based products, listen for the `}<a parentName="p" {...{
          "href": "/docs/api/items/#pending_disconnect"
        }}><inlineCode parentName="a">{`PENDING_DISCONNECT`}</inlineCode></a>{` webhook and send the Item through update mode when receiving it.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "error-handling"
    }}>{`Error handling`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`Sometimes, Plaid API calls may fail due to intermittent outages or connectivity errors at supported institutions. Implement retry logic or error handling as necessary for product API calls.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "link-in-update-mode"
    }}>{`Link in update mode`}</h4>
    <p>{`Update mode is used to fix Items that have entered an error state (for example, because a user changed their password). If your application needs to access an Item repeatedly over a period of time, rather than just immediately after Link, implementing update mode logic is strongly recommended.`}</p>
    <Checkbox mdxType="Checkbox">
      <p>{`Handle the `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{`, `}<inlineCode parentName="p">{`PENDING_DISCONNECT`}</inlineCode>{`, and `}<inlineCode parentName="p">{`PENDING_EXPIRATION`}</inlineCode>{` Item errors or webhooks by launching update mode to ensure your users retain access to their Items. For more information, see `}<a parentName="p" {...{
          "href": "/docs/link/update-mode"
        }}>{`Updating Items via Link`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Listen for the `}<a parentName="p" {...{
          "href": "/docs/api/items/#new_accounts_available"
        }}><inlineCode parentName="a">{`NEW_ACCOUNTS_AVAILABLE`}</inlineCode></a>{` webhook to learn when an Item has a new account associated with it. To request access to that account, launch Link in `}<a parentName="p" {...{
          "href": "/docs/link/update-mode"
        }}>{`update mode`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`(Optional, Auth and Identity only) Implement `}<a parentName="p" {...{
          "href": "/docs/link/update-mode/#resolving-access_not_granted-or-no_auth_accounts-errors-via-product-validations"
        }}>{`Product Validations`}</a>{` in update mode to prevent customers from accidentally revoking required permissions during the update mode flow.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "storage--logging"
    }}>{`Storage & logging`}</h4>
    <p>{`Log Plaid identifiers and IDs properly to enhance security, when contacting Support about a specific request or callback, and for finding specific entries in the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/activity/logs"
      }}>{`Activity Log`}</a>{`. For more information, see `}<a parentName="p" {...{
        "href": "/docs/account/activity"
      }}>{`Dashboard logs and troubleshooting`}</a>{`.`}</p>
    <Checkbox mdxType="Checkbox">
      <p><a parentName="p" {...{
          "href": "/docs/quickstart/glossary/#access-token"
        }}>{`Access tokens`}</a>{` and `}<a parentName="p" {...{
          "href": "/docs/quickstart/glossary/#item-id"
        }}>{`Item IDs`}</a>{` are the core identifiers that map your users to
their financial institutions. Store them securely and associate them with
users of your application. Make sure, however, that these identifiers are
never exposed client-side. Keep in mind that one user can create multiple
Items if they have accounts with multiple financial institutions.`}</p>
      <br />
      <p>{`The same storage requirements apply to other identifiers used instead of access tokens by certain products, such as asset report tokens (used with Assets), payment profile tokens (used with Transfer), and user IDs (or user tokens for legacy integrations) used with Income.`}</p>
    </Checkbox>
    <p>{`Ensure that the following identifiers are securely logged, as they will be needed when contacting Support about a specific request or callback.`}</p>
    <Checkbox mdxType="Checkbox">
      <p><inlineCode parentName="p">{`link_session_id`}</inlineCode>{`: Included in the `}<inlineCode parentName="p">{`onExit`}</inlineCode>{`, `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{`, and `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback of a Link integration.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p><inlineCode parentName="p">{`request_id`}</inlineCode>{`: Included in all Plaid API responses.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p><inlineCode parentName="p">{`account_id`}</inlineCode>{`: Included in all successful Plaid API responses that relate to a specific Item or account.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p><inlineCode parentName="p">{`item_id`}</inlineCode>{`: Included in all successful Plaid API responses that relate to a specific Item.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "item-management"
    }}>{`Item management`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`Delete Items using `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{` when they are no longer being used. For example, you may wish to allow users to remove linked Items through your app's account management interface, or you may want to delete Items when a user deletes their account with your service or becomes inactive, or if the Item has been in an error state for an extended period. Deleting unneeded Items is a security best practice and will also prevent you from being charged for these Items when using a subscription-based product such as Transactions.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "multi-app-use-cases"
    }}>{`Multi-app use cases`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`If you anticipate having multiple apps that use Plaid or having multiple teams at your company using it, consider creating a single shared backend service to handle functions such as logging and token storage in a consolidated place.`}</p>
    </Checkbox>
    <h3 {...{
      "id": "product-specific-recommendations"
    }}>{`Product-specific recommendations`}</h3>
    <h4 {...{
      "id": "auth"
    }}>{`Auth`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`If launching in the US, support the `}<a parentName="p" {...{
          "href": "/docs/auth/coverage/automated/"
        }}>{`Automated Micro-deposits`}</a>{` and `}<a parentName="p" {...{
          "href": "/docs/auth/coverage/same-day/"
        }}>{`Same-Day Micro-deposits`}</a>{` flows for maximum institution coverage.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If supporting the Instant Match, Automated Micro-deposits, or Same-Day Micro-deposits flows, make sure that the country codes parameter provided to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` for these flows includes only the US and no other countries.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If supporting the Automated Micro-deposits flow, make sure to listen for `}<a parentName="p" {...{
          "href": "/docs/api/products/auth/#webhooks"
        }}>{`Auth webhooks`}</a>{` to know when the transaction is completed.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Make sure you are not displaying account numbers in your app's UI, even if truncated or masked, to avoid user confusion when working with institutions that provide virtualized or temporary account numbers. Always use values from the `}<inlineCode parentName="p">{`mask`}</inlineCode>{` field instead.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Listen for the `}<a parentName="p" {...{
          "href": "/docs/api/items/#user_account_revoked"
        }}><inlineCode parentName="a">{`USER_ACCOUNT_REVOKED`}</inlineCode></a>{` and `}<a parentName="p" {...{
          "href": "/docs/api/items/#user_permission_revoked"
        }}><inlineCode parentName="a">{`USER_PERMISSION_REVOKED`}</inlineCode></a>{` webhooks to be notified when a temporary account number has become invalidated. To avoid R04 errors, do not make a transfer using an account number, processor token, or Stripe bank account token that has been revoked. Instead, send the user through update mode to restore the Item; if update mode cannot be launched (which can occur for `}<inlineCode parentName="p">{`USER_PERMISSION_REVOKED`}</inlineCode>{`), create a new Item instead. Then call the auth endpoint again to obtain a new account number or token.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Listen for the `}<a parentName="p" {...{
          "href": "/docs/api/products/auth/#default_update"
        }}><inlineCode parentName="a">{`AUTH: DEFAULT_UPDATE`}</inlineCode></a>{` webhook to be notified when an Item's account or routing number has changed, then call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` or `}<inlineCode parentName="p">{`/processor/auth/get`}</inlineCode>{` again to refresh your information on file.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "balance"
    }}>{`Balance`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`If you are using Balance with non-depository accounts, such as credit card or loan accounts, make sure to specify `}<inlineCode parentName="p">{`min_last_updated_datetime`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` to ensure balance calls to Capital One can succeed.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "identity"
    }}>{`Identity`}</h4>
    <h4 {...{
      "id": "transactions"
    }}>{`Transactions`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`If fetching historical Transactions data using `}<inlineCode parentName="p">{`/transactions/get`}</inlineCode>{` or `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{`, make sure your app implements pagination logic. For sample code, see the `}<a parentName="p" {...{
          "href": "/docs/transactions/add-to-app/#fetching-transaction-data"
        }}>{`integration instructions`}</a>{` or the API Reference for the endpoint.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If you use `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{`, make sure your app properly handles errors that occur during pagination by restarting the pagination loop. For more details, see `}<a parentName="p" {...{
          "href": "/docs/errors/transactions/#transactions_sync_mutation_during_pagination"
        }}><inlineCode parentName="a">{`TRANSACTIONS_SYNC_MUTATION_DURING_PAGINATION`}</inlineCode></a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Handle Transactions webhooks, see `}<a parentName="p" {...{
          "href": "/docs/transactions/webhooks"
        }}>{`Handling Transaction webhooks`}</a>{`.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "investments"
    }}>{`Investments`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`Handle the `}<inlineCode parentName="p">{`HOLDINGS`}</inlineCode>{`-type `}<a parentName="p" {...{
          "href": "/docs/api/products/investments/#holdings-default_update"
        }}><inlineCode parentName="a">{`DEFAULT_UPDATE`}</inlineCode></a>{` webhook if your app needs to keep holdings, values, and balances up-to-date.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Handle the `}<inlineCode parentName="p">{`INVESTMENTS_TRANSACTIONS`}</inlineCode>{`-type `}<a parentName="p" {...{
          "href": "/docs/api/products/investments/#investments_transactions-default_update"
        }}><inlineCode parentName="a">{`DEFAULT_UPDATE`}</inlineCode></a>{` webhook if your app needs to keep Investments transactions up-to-date.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If fetching historical Investments transactions using `}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{`, make sure your app implements pagination logic. For sample code, see the `}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{` API Reference (pagination examples available in Python and Ruby code samples).`}</p>
    </Checkbox>
    <h4 {...{
      "id": "assets"
    }}>{`Assets`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`Handle the `}<inlineCode parentName="p">{`ASSETS`}</inlineCode>{`-type `}<a parentName="p" {...{
          "href": "/docs/api/products/assets/#product_ready"
        }}><inlineCode parentName="a">{`PRODUCT_READY`}</inlineCode></a>{` webhook to know when to call `}<inlineCode parentName="p">{`/asset_report/get`}</inlineCode>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Handle the `}<inlineCode parentName="p">{`ASSETS`}</inlineCode>{`-type `}<a parentName="p" {...{
          "href": "/docs/api/products/assets/#error"
        }}><inlineCode parentName="a">{`ERROR`}</inlineCode></a>{` webhook to gracefully handle errors from failures in `}<inlineCode parentName="p">{`/asset_report/create`}</inlineCode>{`.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "payment-initiation"
    }}>{`Payment Initiation`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`Handle the `}<a parentName="p" {...{
          "href": "/docs/api/products/payment-initiation/#payment_status_update"
        }}><inlineCode parentName="a">{`PAYMENT_STATUS_UPDATE`}</inlineCode></a>{` webhook to keep updated on payment status information.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "virtual-accounts"
    }}>{`Virtual Accounts`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`Handle the `}<a parentName="p" {...{
          "href": "/docs/api/products/virtual-accounts/#wallet_transaction_status_update"
        }}><inlineCode parentName="a">{`WALLET_TRANSACTION_STATUS_UPDATE`}</inlineCode></a>{` webhook to keep updated on transaction status information.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "income"
    }}>{`Income`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`Handle the `}<inlineCode parentName="p">{`INCOME`}</inlineCode>{`-type `}<a parentName="p" {...{
          "href": "/docs/api/products/income/#user_income_verification"
        }}><inlineCode parentName="a">{`USER_INCOME_VERIFICATION`}</inlineCode></a>{` webhook to know when the verification is complete and endpoints that show income or employment related data are ready to be called. Existing integrations that have not migrated should continue handling the legacy `}<a parentName="p" {...{
          "href": "/docs/api/products/income/#income_verification"
        }}><inlineCode parentName="a">{`INCOME_VERIFICATION`}</inlineCode></a>{` webhook.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "identity-verification"
    }}>{`Identity Verification`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`Make sure to update your template IDs when moving to Production; the Sandbox and Production environments have different template IDs.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If you've set any of your Acceptable Risk Levels to "high" in your Risk Rules in order to prevent checks from failing during testing, make sure to change them back to the levels you plan to use in Production.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Implement logic for users to retry identity verification if they have failed.
You can integrate this process directly using the `}<inlineCode parentName="p">{`/identity_verification/retry`}</inlineCode>{`
endpoint or from the Dashboard.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If enabling Selfie Check, make sure that you are `}<a parentName="p" {...{
          "href": "/docs/identity-verification/#mobile-support"
        }}>{`requesting the required camera permissions`}</a>{` on mobile.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`When performing your matching logic, make sure to take into account that `}<inlineCode parentName="p">{`no_data`}</inlineCode>{` is
different from `}<inlineCode parentName="p">{`no_match`}</inlineCode>{`. `}<inlineCode parentName="p">{`no_data`}</inlineCode>{` means that the list issuer didn't supply data
against which to match. `}<inlineCode parentName="p">{`no_match`}</inlineCode>{` means that data was provided by the list issuer
and it did not match the information provided by your customer.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "monitor"
    }}>{`Monitor`}</h4>
    <Checkbox mdxType="Checkbox">
      <p>{`Make sure to implement logic for both onboarding (adding users to a program when they are created) and offboarding (moving users to a program with rescans disabled when they close their account).`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Ensure that the Monitor review queue is checked regularly. For more details, see `}<a parentName="p" {...{
          "href": "/docs/monitor/#building-a-reviewer-workflow"
        }}>{`Building a reviewer workflow`}</a>{` and `}<a parentName="p" {...{
          "href": "/docs/monitor/#preparing-for-ongoing-screening"
        }}>{`preparing for ongoing screening`}</a>{`.`}</p>
    </Checkbox>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "production-setup",
        level: 4,
        title: "Production setup",
      },
{
        id: "link-setup",
        level: 4,
        title: "Link setup",
      },
{
        id: "webhook-configuration",
        level: 4,
        title: "Webhook configuration",
      },
{
        id: "error-handling",
        level: 4,
        title: "Error handling",
      },
{
        id: "link-in-update-mode",
        level: 4,
        title: "Link in update mode",
      },
{
        id: "storage--logging",
        level: 4,
        title: "Storage & logging",
      },
{
        id: "item-management",
        level: 4,
        title: "Item management",
      },
{
        id: "multi-app-use-cases",
        level: 4,
        title: "Multi-app use cases",
      },
{
        id: "product-specific-recommendations",
        level: 3,
        title: "Product-specific recommendations",
      },
{
        id: "auth",
        level: 4,
        title: "Auth",
      },
{
        id: "balance",
        level: 4,
        title: "Balance",
      },
{
        id: "identity",
        level: 4,
        title: "Identity",
      },
{
        id: "transactions",
        level: 4,
        title: "Transactions",
      },
{
        id: "investments",
        level: 4,
        title: "Investments",
      },
{
        id: "assets",
        level: 4,
        title: "Assets",
      },
{
        id: "payment-initiation",
        level: 4,
        title: "Payment Initiation",
      },
{
        id: "virtual-accounts",
        level: 4,
        title: "Virtual Accounts",
      },
{
        id: "income",
        level: 4,
        title: "Income",
      },
{
        id: "identity-verification",
        level: 4,
        title: "Identity Verification",
      },
{
        id: "monitor",
        level: 4,
        title: "Monitor",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
